import React, { useMemo, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosInstance } from 'axios';

import { AuthPageLayout } from './AuthPageLayout';
import { FormInput } from '../../components/Form/FormInput';
import { MainCustomButton, SecondaryCustomButton } from '../../components/Buttons';
import { FormSelect } from '../../components/Form/FormSelect';

import { AuthService } from '../../services/AuthService';

import { errorToast } from '../../utils/toasts';

import { errorMessageAdapter } from '../../adapters';

import { registerValidationSchema } from '../../validation/schemas';

import { RootState } from '../../store';

import { useAppSelector } from '../../hooks/hooksRedux';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useApi } from '../../services/hooks/apiHook';
import {
  OFFSHORE_FLOW_SESSION_KEY,
  OFFSHORE_JURISDICTION_QUERY_PARAM,
  OFFSHORE_JURISDICTION_SESSION_KEY,
  isOffshoreJurisdictionSlug
} from '../../constants/offshoreLpIntent';
interface UserInfos {
  name: string;
  lastName: string;
  pronoum: string;
  email: string;
  password: string;
  confirmPassword: string;
  preferred_language: string;
}

const getRandomInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min) + min); // The maximum is exclusive and the minimum is inclusive
};

const CreateAccountPage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setToken] = useLocalStorage<string | null>('access-token', null);
  const [loading, setLoading] = useState<boolean>(false);

  const api: AxiosInstance = useApi();
  // Validation Schema using Yup with React-Hook-Forms

  const randomImage = useMemo(() => getRandomInt(1, 5), []);

  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);
  const { logo } = useAppSelector((state: RootState) => state.styleGuide.data);

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<UserInfos>({
    resolver: yupResolver(registerValidationSchema),
    mode: 'onChange',
    defaultValues: {
      pronoum: t('options.optPronounSr') ?? ''
    }
  });

  const handleSubmitForm: SubmitHandler<UserInfos> = async (data) => {
    try {
      // Define the post-registration navigation intent as early as possible.
      // This avoids a race where guards may redirect before we set/clear session state.
      const query = new URLSearchParams(location.search);
      const offshoreFlowRaw = query.get('offshore_flow');
      const offshoreFlow = offshoreFlowRaw?.toLowerCase();
      // Only persist LP deep-link intents. Never store 'choose-onboarding' — that poisoned
      // DashboardGuard on later logins in the same tab (stale sessionStorage).
      const sessionIntent = offshoreFlow === 'open' || offshoreFlow === 'transfer' ? offshoreFlow : null;

      const jurisdictionRaw = query.get(OFFSHORE_JURISDICTION_QUERY_PARAM)?.toLowerCase();

      if (typeof window !== 'undefined') {
        if (sessionIntent) {
          sessionStorage.setItem(OFFSHORE_FLOW_SESSION_KEY, sessionIntent);
        } else {
          sessionStorage.removeItem(OFFSHORE_FLOW_SESSION_KEY);
        }
        if (sessionIntent === 'open' && isOffshoreJurisdictionSlug(jurisdictionRaw)) {
          sessionStorage.setItem(OFFSHORE_JURISDICTION_SESSION_KEY, jurisdictionRaw);
        } else {
          sessionStorage.removeItem(OFFSHORE_JURISDICTION_SESSION_KEY);
        }
      }

      setLoading(true);
      const response = await AuthService.create(api, {
        first_name: data.name,
        last_name: data.lastName,
        email: data.email,
        password: data.password,
        password_confirmation: data.confirmPassword,
        pronoun: data.pronoum,
        preferred_language: legalPersonalitySlug
      });
      setLoading(false);
      setToken(response.access_token);

      const nextRoute =
        sessionIntent === 'open'
          ? '/onboarding'
          : sessionIntent === 'transfer'
            ? '/company-transfer'
            : '/choose-onboarding';

      navigate(nextRoute);
    } catch (e: any) {
      setLoading(false);
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  };

  function handleBackButton() {
    navigate('/login');
  }

  return (
    <AuthPageLayout
      imageIndex={randomImage}
      imageText={t('labels.lblWeveHelped')}
      logo={logo}
      title={t('titles.ttlCreateAccount')}
      cardClassName="max-w-[760px]"
    >
      <form className="flex flex-col gap-8" onSubmit={handleSubmit(handleSubmitForm)}>
        <div className="flex flex-col gap-5">
          <div className="flex flex-col items-start justify-between gap-5 md:flex-row">
            <FormInput
              id="name"
              name="name"
              placeholder={t('fields.fldLastNamePlaceholder') || ''}
              label={t('labels.lblFirstName')}
              error={t(errors.name?.message || '')}
              control={control}
            />
            <FormInput
              id="lastName"
              name="lastName"
              placeholder={t('fields.fldLastNamePlaceholder') || ''}
              label={t('labels.lblLastName')}
              error={t(errors.lastName?.message || '')}
              control={control}
            />
          </div>
          <div className="flex flex-col items-start justify-between gap-5 md:flex-row">
            <FormSelect
              label={t('labels.lblPronoun')}
              name="pronoum"
              control={control}
              options={[
                { value: 'sr', label: t('options.optPronounSr') },
                { value: 'sra', label: t('options.optPronounSra') }
              ]}
              error={errors.pronoum?.message}
            />
            <FormInput
              id="email"
              name="email"
              label={t('labels.lblEmail')}
              placeholder={t('fields.fldEmailPlaceholder') || ' '}
              error={t(errors.email?.message || '')}
              control={control}
            />
          </div>
          <div className="flex flex-col items-start justify-between gap-5 md:flex-row">
            <FormInput
              id="password"
              name="password"
              label={t('labels.lblPassword')}
              type="password"
              placeholder={t('fields.fldConfirmPwdPlaceholder') || ' '}
              error={t(errors.password?.message || '')}
              control={control}
              tooltip={t('validations.valPwdAdvice') as string}
            />
            <FormInput
              id="confirmPassword"
              name="confirmPassword"
              label={t('labels.lblConfirmPassword')}
              type="password"
              placeholder={t('fields.fldConfirmPwdPlaceholder') || ' '}
              error={t(errors.confirmPassword?.message || '')}
              control={control}
              tooltip={t('validations.valPwdAdvice') as string}
            />
          </div>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <SecondaryCustomButton type="button" onClick={handleBackButton} text={t('buttons.btnBack')} className="!max-w-none" />
          <MainCustomButton text={t('buttons.btnNext')} loading={loading} className="!max-w-none" />
        </div>
      </form>
    </AuthPageLayout>
  );
};

export { CreateAccountPage };
